// Client-side prediction + server reconciliation.

import { Btn } from "./input";
import { PLAYER_RADIUS, type GridMap } from "./world";
import type { ChunkRegistry } from "./chunks";
import {
  moveAndSlideVoxel, floorAt, ceilingAt, collidesDecor, stairSurfaceTopAt,
  playerCollidesWalls,
  PLAYER_COLLIDER_HEIGHT, PLAYER_COLLIDER_HEIGHT_CROUCH,
  STAIR_STEP_UP_MAX,
} from "./wallCollision";

// Doom-2-темп (≈8.3 м/с у Doomguy). ДОЛЖНО СОВПАДАТЬ с MoveSpeed/StrafeSpeed
// в server/internal/game/player.go — иначе предикшн разъезжается с сервером.
export const MOVE_SPEED = 8.5;       // м/с
export const STRAFE_SPEED = 8.5;
// Doom-momentum (зеркало MoveAccelK/MoveFriction в player.go). Константы
// PER-TICK при 60 Гц: разгон до ~95% за ~9 тиков (150 мс), отпустил клавиши —
// характерное думовское скольжение ~1 м с полной скорости (~0.3 с).
export const MOVE_ACCEL_K = 0.30;
export const MOVE_FRICTION = 0.86;
export const TICK_HZ = 60;
export const TICK_DT = 1 / TICK_HZ;

// Вертикальная физика — ДОЛЖНЫ СОВПАДАТЬ с server/internal/game/player.go
// константами Gravity/JumpVel (иначе клиентское предсказание будет сыпаться
// относительно авторитетного Z сервера).
export const GRAVITY = 20.0;           // м/с²
export const JUMP_VEL = 5.5;           // м/с
export const CROUCH_SPEED_FACTOR = 0.5; // множитель скорости в приседе

// characters: апгрейд «Скорость» локального игрока (Hello.speedMul; сервер —
// p.Char.Loadout.SpeedMul()). applyInput вызывается только для своего игрока.
let localSpeedMul = 1;
export function setLocalSpeedMultiplier(m: number): void {
  localSpeedMul = m > 0 ? m : 1;
}
/** Параметры визуального сглаживания Z (камера + модели игроков/NPC).
 *
 *  Сама физика мгновенна (server и client predict следуют HF лестницы точно,
 *  без сглаживания) — это разделение physics/visual работает как Doom-2
 *  view-bob: точная коллизия + плавная камера.
 *
 *  Используется **2nd-order critically damped smoothing** (smooth-damp,
 *  Game Programming Gems / Unity Mathf.SmoothDamp). Почему не 1st-order
 *  exp-lerp:
 *
 *  - 1st-order exp-lerp при step-входе (мгновенный δZ=0.5м) даёт пиковую
 *    скорость камеры 7+ м/с с **мгновенным** скачком velocity 0 → peak в
 *    одном кадре (acceleration → ∞). Игрок ощущает это как «выстрел камеры
 *    вниз» с резким стартом — отсюда «бампинг при входе на лестницу».
 *
 *  - 2nd-order critically damped имеет **zero initial velocity**: в кадре
 *    входа velocity = 0, потом плавно ramping up. Acceleration ограничена
 *    `~h/τ²` (для δZ=0.5м τ=0.15с — ~22 м/s²; против 384 м/s² у 1st-order
 *    с τ=0.07с). Игрок ощущает это как ЕСТЕСТВЕННОЕ падение тела с
 *    ускорением, а не толчок.
 *
 *  - Peak velocity: ~0.5·h/smoothTime = 0.5·0.5/0.15 = 1.7 м/с — близко
 *    к скорости ходьбы, не «швыряет».
 *
 *  - Steady-state lag при ramp input R = R·smoothTime/2 = 3·0.15/2 = 0.22м.
 *    В FP не видно (нет наземных reference points).
 *
 *  - 3-Гц альтернация склон↔плато на лестнице затухает на ~85% (выше чем
 *    у 1st-order τ=0.07 с её 76%) — ещё чище средней-частоты bumps.
 *
 *  smoothTime=0.15с — компромисс: меньше = резче на step-входах, больше =
 *  больше lag и settling time. Settling time (5% to target) ~ 5·smoothTime
 *  = 750ms. Δz=0.5м drop проходит через "мягкий" профиль за ~600ms. */
export const VISUAL_FEET_SMOOTH_TIME = 0.15;
/** Жёсткий cap скорости камеры (м/с) — страховка для очень крупных δZ.
 *  Соответствует ~быстрому шагу человека вверх по холму. */
export const VISUAL_FEET_SMOOTH_MAX_SPEED = 7.0;
/** Если |δZ| > этого — снап без сглаживания (телепорт/респавн/portal). */
export const VISUAL_FEET_SMOOTH_SNAP = 1.5;
/** Legacy alias — оставлен для совместимости с remote-player smoothing
 *  (используется как rate-cap fallback). */
export const VISUAL_FEET_SMOOTH_SPEED = VISUAL_FEET_SMOOTH_MAX_SPEED;
/** Порог «нет опоры» на земле (сервер GroundedDropUngroundTol). */
export const GROUNDED_DROP_UNGROUND_TOL = 0.38;

export interface PredictedState {
  x: number;
  y: number;
  /** высота ступней (0 = пол арены, > 0 — на платформе или в прыжке) */
  z: number;
  /** горизонтальная скорость (Doom-momentum; зеркало server Player.Vx/Vy) */
  vx: number;
  vy: number;
  /** вертикальная скорость */
  vz: number;
  /** находится ли игрок на опоре (пол или верх декорации) */
  grounded: boolean;
  /** удерживает ли игрок присед (низкий коллайдер, замедленная скорость).
   *  true даже если пользователь отпустил C, но над головой низкий потолок. */
  crouching: boolean;
  /** Был ли игрок на лестнице на прошлом тике (зеркало server `StairContactPrev`).
   *  Нужно для 1-tick grace при выходе с лестницы — чтобы physics-lag сгладился
   *  до перехода на flat-floor target. */
  stairContactPrev: boolean;
  yaw: number;
  pitch: number;
}

export interface InputRecord {
  seq: number;
  buttons: number;
  /** предыдущие кнопки (до buttons) — нужны для edge-trigger прыжка
   *  при replay'е после reconciliation. */
  prevButtons: number;
  yaw: number;
  pitch: number;
  dt: number;
  /** состояние ПОСЛЕ применения этого input */
  resultX: number;
  resultY: number;
  resultZ: number;
  resultVz: number;
  resultGrounded: boolean;
  resultCrouching: boolean;
  resultStairContactPrev: boolean;
}

export class PredictionBuffer {
  /** ring buffer */
  private records: InputRecord[] = [];
  private capacity = 256;

  add(rec: InputRecord): void {
    this.records.push(rec);
    if (this.records.length > this.capacity) this.records.shift();
  }

  clear(): void { this.records.length = 0; }

  /** Удалить все записи с seq <= ackedSeq. */
  ack(ackedSeq: number): void {
    let i = 0;
    while (i < this.records.length && this.records[i].seq <= ackedSeq) i++;
    if (i > 0) this.records.splice(0, i);
  }

  /** Реплей оставшихся инпутов начиная от заданного состояния. */
  replay(state: PredictedState, map: GridMap, chunks: ChunkRegistry, damaged: Set<number>): PredictedState {
    let { x, y, z, vx, vy, vz, grounded, crouching, stairContactPrev, yaw, pitch } = state;
    for (const rec of this.records) {
      const r = applyInput(map, chunks, damaged,
        x, y, z, vx, vy, vz, grounded, crouching, stairContactPrev,
        rec.buttons, rec.prevButtons, rec.yaw, rec.dt);
      x = r.x; y = r.y; z = r.z;
      vx = r.vx; vy = r.vy; vz = r.vz; grounded = r.grounded;
      crouching = r.crouching;
      stairContactPrev = r.stairContactPrev;
      yaw = rec.yaw; pitch = rec.pitch;
      rec.resultX = x; rec.resultY = y;
      rec.resultZ = z; rec.resultVz = vz; rec.resultGrounded = grounded;
      rec.resultCrouching = crouching;
      rec.resultStairContactPrev = stairContactPrev;
    }
    return { x, y, z, vx, vy, vz, grounded, crouching, stairContactPrev, yaw, pitch };
  }

  size(): number { return this.records.length; }
}

export interface ApplyInputResult {
  x: number;
  y: number;
  z: number;
  vx: number;
  vy: number;
  vz: number;
  grounded: boolean;
  crouching: boolean;
  stairContactPrev: boolean;
}


export function applyInput(
  map: GridMap,
  chunks: ChunkRegistry,
  damaged: Set<number>,
  x: number, y: number, z: number, vx: number, vy: number, vz: number,
  grounded: boolean, crouching: boolean,
  stairContactPrev: boolean,
  buttons: number, prevButtons: number, yaw: number, dt: number,
): ApplyInputResult {
  // ── Присед: определяем целевое состояние ───────────────────────────
  // Отпустил C под потолком — не даём встать (голова ударилась бы в балку).
  let nCrouch = (buttons & Btn.Crouch) !== 0;
  if (crouching && !nCrouch) {
    if (collidesDecor(chunks, x, y, z, PLAYER_RADIUS, PLAYER_COLLIDER_HEIGHT)) {
      nCrouch = true;
    }
  }
  const colliderH = nCrouch ? PLAYER_COLLIDER_HEIGHT_CROUCH : PLAYER_COLLIDER_HEIGHT;
  const speedScale = (nCrouch ? CROUCH_SPEED_FACTOR : 1.0) * localSpeedMul; // characters

  // ── Горизонталь: Doom-momentum (зеркало server applyInput) ─────────
  // Кнопки задают wish-скорость; фактическая скорость догоняет её с
  // MOVE_ACCEL_K за тик, без ввода гаснет трением MOVE_FRICTION — резкий
  // разгон + характерное думовское скольжение при остановке/развороте.
  let dx = 0, dy = 0;
  if (buttons & Btn.Forward) { dx += Math.cos(yaw); dy += Math.sin(yaw); }
  if (buttons & Btn.Back)    { dx -= Math.cos(yaw); dy -= Math.sin(yaw); }
  if (buttons & Btn.Right)   { dx += Math.sin(yaw); dy -= Math.cos(yaw); }
  if (buttons & Btn.Left)    { dx -= Math.sin(yaw); dy += Math.cos(yaw); }
  const len = Math.hypot(dx, dy);
  let nvx = vx, nvy = vy;
  if (len > 0) {
    const wishX = (dx / len) * MOVE_SPEED * speedScale;
    const wishY = (dy / len) * MOVE_SPEED * speedScale;
    nvx += (wishX - nvx) * MOVE_ACCEL_K;
    nvy += (wishY - nvy) * MOVE_ACCEL_K;
  } else {
    nvx *= MOVE_FRICTION;
    nvy *= MOVE_FRICTION;
    if (nvx * nvx + nvy * nvy < 0.0004) { nvx = 0; nvy = 0; }
  }
  const [nx, ny, stepUpZ] = moveAndSlideVoxel(map, chunks, damaged, x, y, z, colliderH, nvx * dt, nvy * dt, PLAYER_RADIUS);

  // ── Вертикаль: edge-trigger прыжка + интеграция + опора ───────────
  const jumpPressed = (buttons & Btn.Jump) !== 0;
  const jumpWasPressed = (prevButtons & Btn.Jump) !== 0;
  let nz = z;
  let nvz = vz;
  let nGrounded = grounded;
  // Step-up: moveAndSlideVoxel поднял игрока на лестницу/порог.
  if (stepUpZ > 0) {
    nz = stepUpZ;
    nvz = 0;
    nGrounded = true;
  }
  if (jumpPressed && !jumpWasPressed && nGrounded) {
    nvz = JUMP_VEL;
    nGrounded = false;
  }
  if (!nGrounded) {
    const prevZ = nz;
    nvz -= GRAVITY * dt;
    nz += nvz * dt;
    const floor = floorAt(chunks, nx, ny, PLAYER_RADIUS, prevZ + 0.01);
    if (nz <= floor) {
      nz = floor;
      nvz = 0;
      nGrounded = true;
    }
    // Упёрся макушкой в балку — гасим Vz и клампим Z.
    if (nvz > 0) {
      const ceil = ceilingAt(chunks, nx, ny, PLAYER_RADIUS, nz);
      if (nz + colliderH > ceil) {
        nz = ceil - colliderH;
        if (nz < floor) nz = floor;
        nvz = 0;
      }
    }
  } else {
    const floor = floorAt(chunks, nx, ny, PLAYER_RADIUS, nz + STAIR_STEP_UP_MAX + 0.05);
    const onStair = stairSurfaceTopAt(chunks, nx, ny, PLAYER_RADIUS) > -Infinity;
    // Adaptive ungrounding-порог (зеркало server applyInput): на лестнице
    // ИЛИ один тик после выхода с неё (stairContactPrev) допускаем step-down
    // до STAIR_STEP_UP_MAX (0.9м) без gravity fall. Grace-период критичен
    // для больших ступеней: на стыке «лестница → пол» onStair может упасть
    // в false раньше, чем пол выровняется → с tol=0.38 ungrounding → bounce.
    let tol = GROUNDED_DROP_UNGROUND_TOL;
    if (onStair || stairContactPrev) {
      tol = STAIR_STEP_UP_MAX;
    }
    if (nz - floor > tol) {
      nGrounded = false;
    } else if (floor <= nz || !playerCollidesWalls(map, chunks, damaged, nx, ny, floor, PLAYER_RADIUS, colliderH)) {
      // Подъём ног к ступени разрешён, только если цилиндр на новой высоте
      // свободен (зеркало сервера): стена 2эт стоит на кромке марша, и
      // безусловная подтяжка вдавливала макушку в её кубы («вклеивание»).
      nz = floor;
    }
    stairContactPrev = onStair;
  }
  return { x: nx, y: ny, z: nz, vx: nvx, vy: nvy, vz: nvz, grounded: nGrounded, crouching: nCrouch, stairContactPrev };
}
